import { Icon } from '@chakra-ui/react';
import { CustomerPortalSessionData } from '@monetize/types/app';
import { Maybe } from '@monetize/types/common';
import { ReactNode, useMemo } from 'react';
import { MdOutlineArrowBack } from 'react-icons/md';
import { useLocation, useParams } from 'react-router';
import { Link as ReactDomLink } from 'react-router-dom';
import { CUSTOMER_PORTAL_PREVIEW_ACCESS_TOKEN } from '../../../api/customerPortalService';
import { CustomerPortalShareLinkCopyButton } from '../../../components/CustomerPortal/CustomerPortalShareLinkCopyButton';
import {
  MAlert,
  MBox,
  MFlex,
  MLink,
  MText,
} from '../../../components/Monetize';
import { CopyToClipboardButton } from '../../../components/Monetize/CopyToClipboardButton';
import { getBillGroupPageRoute } from '../../../constants/routes';
import { logger } from '../../../services/logger';
import { CustomerPortalFooter } from './components/CustomerPortalFooter';

function decodeSessionParams(sessionData: Maybe<string>):
  | { success: true; tenantId: string; accountId: string; billGroupId: string }
  | {
      success: false;
      error: string;
      tenantId?: never;
      accountId?: never;
      billGroupId?: never;
    } {
  try {
    if (!sessionData) {
      return {
        success: false,
        error: 'Missing Session Data',
      };
    }
    return {
      success: true,
      ...(JSON.parse(atob(sessionData)) as {
        tenantId: string;
        accountId: string;
        billGroupId: string;
      }),
    };
  } catch (ex) {
    logger.error('Failed to decode session data', ex);
    return {
      success: false,
      error: 'Invalid Session Data',
    };
  }
}

interface CustomerPortalAuthenticatedLayoutProps {
  children: (
    sessionData: CustomerPortalSessionData,
    onLogout?: () => void,
  ) => ReactNode;
}

export const CustomerPortalAuthenticatedLayout = ({
  children,
}: CustomerPortalAuthenticatedLayoutProps) => {
  const pathParams = useParams();
  const location = useLocation();
  const sessionId = pathParams.sessionId as string;

  const decodedParams = useMemo(
    () => decodeSessionParams(pathParams.sessionData),
    [pathParams.sessionData],
  );

  const isPreviewMode = useMemo(
    () => location.pathname.includes('/preview/'),
    [location.pathname],
  );

  if (!decodedParams.success) {
    return (
      <MBox minH="100vh" display="flex" flexDirection="column">
        <MFlex
          flex={1}
          alignItems="center"
          justifyContent="center"
          px={4}
          py={8}
        >
          <MBox
            bg="white"
            borderRadius="lg"
            p={8}
            maxW="400px"
            w="100%"
            textAlign="center"
            boxShadow="md"
          >
            <MText fontSize="xl" fontWeight="600" color="gray.800" mb={2}>
              Invalid Link
            </MText>
            <MText color="gray.500" fontSize="sm" mb={4}>
              {decodedParams.error ||
                'This customer portal link appears to be invalid.'}
            </MText>
            <MText color="gray.400" fontSize="xs">
              Please check the link you received and try again, or contact
              support for assistance.
            </MText>
          </MBox>
        </MFlex>
        <CustomerPortalFooter />
      </MBox>
    );
  }

  const { tenantId, accountId, billGroupId } = decodedParams;
  const billGroupLink = `${getBillGroupPageRoute(billGroupId)}?customerPortalOpen=true`;

  return (
    <MBox bg="tGray.shareBack">
      <MAlert
        omitIcon
        type="info"
        alertProps={{ py: 2, px: 0, borderRadius: 0 }}
      >
        <MBox
          maxW="80rem"
          mx="auto"
          px={{ base: 4, md: 8 }}
          display="grid"
          gridTemplateColumns="1fr auto 1fr"
          alignItems="center"
          gap={4}
          width="100%"
        >
          <MBox justifySelf="start">
            {isPreviewMode && (
              <MLink
                asChild
                fontSize="sm"
                fontWeight={600}
                display="flex"
                alignItems="center"
                gap={1}
              >
                <ReactDomLink to={billGroupLink}>
                  <Icon boxSize={4} asChild>
                    <MdOutlineArrowBack />
                  </Icon>
                  Go Back
                </ReactDomLink>
              </MLink>
            )}
          </MBox>
          <MText fontWeight={600} fontSize="md" justifySelf="center">
            {isPreviewMode ? 'Customer Portal Preview' : 'Customer Portal'}
          </MText>
          <MBox justifySelf="end">
            {isPreviewMode ? (
              <CustomerPortalShareLinkCopyButton
                accountId={accountId}
                billGroupId={billGroupId}
                beforeCopyText="Copy Public Portal Link"
                afterCopyText="Copied"
              />
            ) : (
              <CopyToClipboardButton
                value={window.location.href}
                beforeCopyText="Copy Portal Link"
                afterCopyText="Copied"
              />
            )}
          </MBox>
        </MBox>
      </MAlert>
      {children(
        {
          tenantId,
          accountId,
          billGroupId,
          sessionId,
          accessToken: isPreviewMode
            ? CUSTOMER_PORTAL_PREVIEW_ACCESS_TOKEN
            : '', // Public view will have actual accessToken from session
          email: '',
        },
        // No logout in preview mode; controls are hidden downstream.
        undefined,
      )}
    </MBox>
  );
};
